import { Box, IconButton, Typography } from "@mui/material"
import { AddOutlined, DeleteOutlined } from "@mui-symbols-material/w300"
import type { ReactElement } from "react"
import { useFieldArray, useFormContext } from "react-hook-form"

import { MAX_ALLOWED_PARTS } from "../constants"
import type { PaymentTermsSchemaFormValues } from "../schemas/PaymentTermsSchema"

import AddPaymentParts from "./AddPaymentParts"

import { Button } from "@/components/common/Button"
import { PaymentRuleEventGql } from "@/graphql/codegen/graphql"

const AddPaymentOption = ({
  optionIndex = 0,
  isAllowedToRemoveOption,
  onRemove,
}: {
  optionIndex: number
  isAllowedToRemoveOption: boolean
  onRemove: (index: number) => void
}): ReactElement => {
  const { control } = useFormContext<PaymentTermsSchemaFormValues>()
  const {
    fields: partFields,
    append: appendParts,
    remove: removeParts,
  } = useFieldArray<PaymentTermsSchemaFormValues>({
    control,
    name: `options.${optionIndex}.parts`,
  })

  const isAllowedToRemovePart = partFields.length > 1

  const handleAddParts = () => {
    appendParts({
      paymentRule: {
        daysAfter: "",
        event: PaymentRuleEventGql.PlacingOrder,
        percentage: "",
      },
      includeSalesDiscount: false,
      discount: {
        percentage: "",
        applicableIfWithinDays: "",
      },
    })
  }

  const handleRemoveParts = (index: number) => {
    removeParts(index)
  }

  return (
    <Box className='rounded-md border border-solid border-gray-300 bg-gray-100 px-6 py-11'>
      <Box className='flex items-center justify-between gap-3 pb-3'>
        <Typography variant='subtitle1' className='pb-4 text-lg text-gray-900'>
          Option {optionIndex + 1}
        </Typography>
        <Box className='flex gap-3'>
          <Button
            appearance='outlined'
            startIcon={<AddOutlined />}
            disabled={partFields.length >= MAX_ALLOWED_PARTS}
            onClick={handleAddParts}
          >
            Add Payment Term Part
          </Button>
          {isAllowedToRemoveOption && (
            <IconButton aria-label='delete parts' onClick={() => onRemove(optionIndex)}>
              <DeleteOutlined />
            </IconButton>
          )}
        </Box>
      </Box>
      <Box className='space-y-6'>
        {partFields.map((part, index) => (
          <AddPaymentParts
            partsIndex={index}
            optionsIndex={optionIndex}
            isAllowedToRemovePart={isAllowedToRemovePart}
            onRemove={handleRemoveParts}
            key={part.id}
          />
        ))}
      </Box>
    </Box>
  )
}

export default AddPaymentOption
